/**
 * Orchestrazione del player: avvio (anche offline), pairing, timer (heartbeat / manifest / orologio),
 * comandi, schermate di stato, adozione del manifest nuovo, download e GC degli asset.
 */
import { capCapabilities, detectCapabilitiesFromBrowser, detectPlatform } from './capabilities';
import type { Config } from './config';
import { ErrorLog, EventLog } from './diag';
import { sanitizeManifest } from './manifest';
import {
  ApiClient, AuthError, backoffMs, DeviceDisabledError, SubscriptionInactiveError,
  type Command, type PairStartResponse,
} from './net/api';
import { SyncedClock } from './net/clock';
import { NowNotifier } from './reporting/now';
import { PlayBuffer, PlayRecorder, PlayReporter } from './reporting/plays';
import { captureJpeg, runScreenshotCommand, type CanvasLike } from './reporting/screenshot';
import { canAdopt, neededAssetIds } from './playback/adopt';
import { PlaybackController } from './playback/controller';
import { DomSurfaces } from './playback/surfaces';
import { SYSTEM_SCHEDULER } from './playback/types';
import { AssetManager, type DownloadTarget } from './store/asset-manager';
import { openAssetStore, type AssetStore } from './store/asset-cache';
import { bootWithRetry } from './startup';
import { clearProvisionedToken, loadProvisionedToken, saveProvisionedToken } from './store/provision';
import { openKv, PlayerState } from './store/state';
import type { Manifest } from './types';
import { DiagOverlay, Screens, type DiagLines } from './ui/screens';

export const HEARTBEAT_MS = 60_000;
export const MANIFEST_MS = 60_000;
export const CLOCK_MS = 10 * 60_000;
export const DISABLED_RETRY_MS = 60_000;
export const INACTIVE_RETRY_MS = 5 * 60_000;
const GC_MS = 60 * 60_000;
const ADOPT_CHECK_MS = 5_000;
const URL_REFRESH_MIN_GAP_MS = 30_000;

type AppPhase = 'booting' | 'pairing' | 'running' | 'disabled' | 'inactive';

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

interface PendingManifest {
  manifest: Manifest;
  raw: unknown;
  etag: string | null;
  since: number;
}

export class PlayerApp {
  readonly events = new EventLog();
  readonly errors = new ErrorLog();

  private readonly api: ApiClient;
  private readonly clock = new SyncedClock();
  private readonly screens: Screens;

  /** Host mostrato nell'istruzione di abbinamento (come l'app Android). */
  private hostForInstructions(): string {
    return this.cfg.apiBase.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }
  private readonly diag: DiagOverlay;
  private readonly surfaces: DomSurfaces;
  private controller!: PlaybackController;
  private state!: PlayerState;
  private assets!: AssetManager;
  private store!: AssetStore;
  private persistent = true;
  private storagePersisted: boolean | null = null; // esito di navigator.storage.persist(); null = non disponibile / non ancora chiesto
  private persistAsked = false;

  private phase: AppPhase = 'booting';
  private token: string | null = null;
  private device: { id: number; name: string } | null = null;
  private current: Manifest | null = null;
  private currentRaw: unknown = null;
  private currentEtag: string | null = null;
  private pending: PendingManifest | null = null;
  private lastReceivedEtag: string | null = null;
  private lastHeartbeatOkAt: number | null = null; // Date.now() dell'ultimo heartbeat riuscito (debug)
  private heartbeatOk = false;
  private readonly seenCommands = new Set<number>();
  private lastUrlRefresh = 0;
  private loopGen = 0;
  private pairingRun = 0;
  private timers: unknown[] = [];
  private readonly caps: ReturnType<typeof detectCapabilitiesFromBrowser>;
  private readonly loopTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly platform = detectPlatform();

  private readonly recorder: PlayRecorder;
  private readonly nowNotifier: NowNotifier;
  private reporter: PlayReporter | null = null;

  constructor(private readonly cfg: Config, root: HTMLElement) {
    this.caps = capCapabilities(detectCapabilitiesFromBrowser(), cfg.maxHeight);
    this.api = new ApiClient({ baseUrl: cfg.apiBase });
    // il recorder osserva show/hide (proof-of-play) e inoltra tutto all'EventLog di debug
    this.nowNotifier = new NowNotifier(
      async (body) => {
        if (this.token && this.phase === 'running') await this.api.now(this.token, body);
      },
      (e) => { this.onLoopError('now', e, 0); },
    );
    this.recorder = new PlayRecorder({
      clock: this.clock,
      next: this.events,
      onShow: (i) => this.nowNotifier.show(i),
      onBlack: (startedAt) => this.nowNotifier.black(startedAt),
      onRecord: (rec) => {
        this.reporter?.add(rec).catch((e: unknown) => this.errors.add(`plays: ${e instanceof Error ? e.message : String(e)}`));
      },
    });
    this.surfaces = new DomSurfaces(root, { events: this.recorder, log: (m) => this.errors.add(m) });
    this.screens = new Screens(root);
    this.diag = new DiagOverlay(root);
  }

  // --- avvio ---------------------------------------------------------------

  /** Avvio con rete di sicurezza: se `boot` fallisce si mostra l'errore e si riprova ogni 30 s (mai nero muto). */
  async start(): Promise<void> {
    this.installInput();
    await bootWithRetry({
      boot: () => this.boot(),
      show: (message, retryInS) => this.screens.showStartError(message, retryInS),
      sleep: (ms) => sleep(ms),
      log: (e) => this.errors.add(`avvio: ${e instanceof Error ? e.message : String(e)}`),
    });
  }

  private async boot(): Promise<void> {
    this.stopLoops(); // idempotente: azzera timer di un tentativo precedente (e ricrea quello della diagnostica)
    await this.requestPersist();
    const { kv, persistent } = await openKv();
    this.persistent = persistent;
    this.state = new PlayerState(kv);
    this.reporter = new PlayReporter(
      new PlayBuffer(kv),
      {
        sendPlays: async (batch) => {
          if (!this.token) throw new Error('nessun token');
          await this.api.plays(this.token, batch);
        },
      },
      (m) => this.errors.add(m),
    );
    this.store = await openAssetStore((m) => this.errors.add(m));
    this.assets = new AssetManager({
      store: this.store,
      onChange: () => { this.controller.assetsChanged(); this.tryAdopt(); },
      onUrlProblem: () => { void this.refreshUrls(); },
      onError: (m) => this.errors.add(m),
      loadUnreferenced: () => this.state.getUnreferencedSince(),
      saveUnreferenced: (v) => this.state.setUnreferencedSince(v),
    });
    await this.assets.init();
    this.controller = new PlaybackController({
      surfaces: this.surfaces,
      assets: {
        isReady: (id, sha) => this.assets.isReady(id, sha),
        getUrl: (id, sha) => this.assets.getObjectUrl(id, sha),
      },
      clock: this.clock,
      scheduler: SYSTEM_SCHEDULER,
      events: this.recorder,
      onLeaving: (completed) => this.recorder.leaving(completed),
      log: (m) => this.errors.add(m),
    });

    const off = await this.state.getClockOffset();
    if (off !== undefined) this.clock.setOffset(off);

    if (this.cfg.debug) {
      this.diag.set(true);
      this.exposeDebugHooks();
    }

    this.token = (await this.state.getToken()) ?? null;
    if (!this.token && this.cfg.provisionUrl) {
      // profilo perso/svuotato: il token può essere nel server locale (Raspberry kiosk), niente riabbinamento
      const t = await loadProvisionedToken(this.cfg.provisionUrl);
      if (t) {
        await this.state.setToken(t);
        this.token = t;
        this.errors.add('token recuperato dal provisioning locale');
      }
    } else if (this.token && this.cfg.provisionUrl) {
      // allinea il file locale (player abbinati prima che esistesse), senza bloccare l'avvio
      const url = this.cfg.provisionUrl;
      const token = this.token;
      void loadProvisionedToken(url)
        .then((t) => (t !== token ? saveProvisionedToken(url, token) : true))
        .catch(() => undefined);
    }
    // con il token dal provisioning `device` resta null (il nome arriva solo dal pairing): la diagnostica mostra "non abbinato"
    this.device = (await this.state.getDevice()) ?? null;
    if (this.token) await this.enterRunning();
    else void this.runPairing();
  }

  /**
   * Chiede lo storage persistente (una sola volta per sessione, best effort): senza `persist` Chromium può
   * svuotare cache e IndexedDB del sito sotto pressione di spazio.
   */
  private async requestPersist(): Promise<void> {
    if (this.persistAsked) return;
    this.persistAsked = true;
    const st = typeof navigator !== 'undefined' ? navigator.storage : undefined;
    if (!st?.persist) return;
    try {
      this.storagePersisted = (await st.persisted?.()) === true || (await st.persist());
    } catch {
      this.storagePersisted = false;
    }
  }

  // --- input -----------------------------------------------------------------

  private installInput(): void {
    let fsTried = false;
    const gesture = () => {
      this.surfaces.userGesture();
      if (!fsTried && !document.fullscreenElement) {
        fsTried = true;
        document.documentElement.requestFullscreen?.().catch(() => undefined);
      }
    };
    window.addEventListener('keydown', (e) => {
      gesture();
      if (e.key === 'd' || e.key === 'D' || e.key === 'Info' || e.keyCode === 457) { // 457 = tasto INFO dei telecomandi TV
        this.diag.toggle();
        this.diag.update(this.diagLines());
      }
    });
    window.addEventListener('pointerdown', gesture);
    // pagina che si chiude/ricarica: i contenuti ancora in onda risultano interrotti (best effort)
    window.addEventListener('pagehide', () => this.recorder.closeAll());
    // la rete è tornata: non aspettare il backoff (fino a 5 min), risincronizza subito
    window.addEventListener('online', () => {
      if (this.phase !== 'running' || !this.token) return;
      this.stopLoops();
      this.startRunningLoops();
    });
    window.addEventListener('error', (e) => this.errors.add(`js: ${e.message}`));
    window.addEventListener('unhandledrejection', (e) => this.errors.add(`promise: ${String(e.reason)}`));
  }

  // --- pairing -------------------------------------------------------------

  private async runPairing(): Promise<void> {
    const run = ++this.pairingRun;
    this.stopLoops();
    this.phase = 'pairing';
    this.controller.stop();
    let attempt = 0;
    while (run === this.pairingRun) {
      let start: PairStartResponse;
      try {
        start = await this.api.pairStart({
          platform: this.platform,
          model: navigator.userAgent.slice(0, 120),
          app_version: __APP_VERSION__,
          capabilities: this.caps,
        });
      } catch (e) {
        this.errors.add(`pair/start: ${e instanceof Error ? e.message : String(e)}`);
        this.screens.showPairing(null, 'Connessione al server…', this.hostForInstructions());
        await sleep(backoffMs(attempt++));
        continue;
      }
      attempt = 0;
      this.screens.showPairing(start.code, 'In attesa dell\'abbinamento…', this.hostForInstructions());
      let ttl = Date.parse(start.expires_at) - Date.now();
      if (!(ttl > 0 && ttl <= 15 * 60_000)) ttl = 10 * 60_000; // orologio locale sballato: uso i 10 minuti da spec
      const until = Date.now() + ttl;
      const pollMs = Math.max(1, start.poll_interval_s || 3) * 1000;
      while (run === this.pairingRun && Date.now() < until) {
        await sleep(pollMs);
        if (run !== this.pairingRun) return;
        try {
          const st = await this.api.pairStatus(start.poll_token);
          if (st.status === 'paired') {
            this.token = st.device_token;
            this.device = st.device;
            await this.state.setToken(st.device_token);
            await this.state.setDevice(st.device);
            if (this.cfg.provisionUrl) void saveProvisionedToken(this.cfg.provisionUrl, st.device_token);
            this.pairingRun++;
            await this.enterRunning();
            return;
          }
          if (st.status === 'restart') break;
        } catch (e) {
          this.errors.add(`pair/status: ${e instanceof Error ? e.message : String(e)}`); // rete: si continua a interrogare
        }
      }
    }
  }

  // --- esecuzione ----------------------------------------------------------

  private async enterRunning(): Promise<void> {
    this.phase = 'running';
    this.screens.hide();
    // 1) parte SUBITO offline con manifest e asset salvati
    const saved = await this.state.getManifest();
    if (saved) {
      const { manifest } = sanitizeManifest(saved.manifest);
      if (manifest) this.adopt(manifest, saved.manifest, saved.etag);
    }
    this.controller.start();
    // 2) poi sincronizza, senza mai bloccarsi sulla rete
    this.startRunningLoops();
  }

  private startRunningLoops(): void {
    this.startLoop('clock', () => this.syncClock(), () => CLOCK_MS, false);
    this.startLoop('heartbeat', () => this.heartbeat(), () => (this.phase === 'inactive' ? INACTIVE_RETRY_MS : (this.cfg.hbMs ?? HEARTBEAT_MS)), true);
    this.startLoop('manifest', () => this.fetchManifest(false), () => (this.phase === 'inactive' ? INACTIVE_RETRY_MS : MANIFEST_MS), true);
    this.timers.push(setInterval(() => this.tryAdopt(), ADOPT_CHECK_MS));
    this.timers.push(setInterval(() => { void this.assets.gc().catch(() => undefined); }, GC_MS));
  }

  private stopLoops(): void {
    this.loopGen++;
    for (const t of this.timers) clearInterval(t as ReturnType<typeof setInterval>);
    for (const t of this.loopTimers.values()) clearTimeout(t);
    this.loopTimers.clear();
    this.timers = [];
    this.timers.push(setInterval(() => this.diag.update(this.diagLines()), 1000));
  }

  /** `recovers`: solo le chiamate AUTENTICATE possono far uscire da "disattivato"/"abbonamento non attivo". */
  private startLoop(name: string, run: () => Promise<void>, intervalMs: () => number, recovers: boolean): void {
    const gen = this.loopGen;
    let fails = 0;
    const tick = async (): Promise<void> => {
      if (gen !== this.loopGen) return;
      let delay: number;
      try {
        await run();
        fails = 0;
        if (recovers) this.recoverIfNeeded();
        delay = intervalMs();
      } catch (e) {
        delay = this.onLoopError(name, e, fails++);
      }
      if (gen !== this.loopGen) return;
      this.loopTimers.set(name, setTimeout(() => { void tick(); }, delay));
    };
    void tick();
  }

  /** Gestione centralizzata degli errori delle API autenticate (API.md §2 e §3). */
  private onLoopError(name: string, e: unknown, fails: number): number {
    if (e instanceof AuthError) {
      void this.resetToPairing();
      return HEARTBEAT_MS;
    }
    if (e instanceof DeviceDisabledError) {
      this.setBlocked('disabled');
      return DISABLED_RETRY_MS;
    }
    if (e instanceof SubscriptionInactiveError) {
      this.setBlocked('inactive');
      return INACTIVE_RETRY_MS;
    }
    // rete / 5xx: NON si cancella nulla, si riprova con backoff 5 s -> 5 min
    this.errors.add(`${name}: ${e instanceof Error ? e.message : String(e)}`);
    return backoffMs(fails);
  }

  private setBlocked(kind: 'disabled' | 'inactive'): void {
    if (this.phase === kind) return;
    this.phase = kind;
    this.controller.stop();
    if (kind === 'disabled') this.screens.showDisabled();
    else this.screens.showInactive();
  }

  private recoverIfNeeded(): void {
    if (this.phase !== 'disabled' && this.phase !== 'inactive') return;
    this.phase = 'running';
    this.screens.hide();
    this.controller.setManifest(this.current);
    this.controller.start();
  }

  /** 401: token revocato -> cancella token e cache dei media, torna al pairing. */
  private async resetToPairing(): Promise<void> {
    if (this.phase === 'pairing') return;
    this.errors.add('401: token non valido, torno al pairing');
    this.stopLoops();
    this.controller.stop();
    this.current = null;
    this.pending = null;
    this.currentEtag = null;
    this.lastReceivedEtag = null;
    this.token = null;
    this.device = null;
    // prima di tornare al pairing: al riavvio non si deve ricaricare un token revocato
    if (this.cfg.provisionUrl) await clearProvisionedToken(this.cfg.provisionUrl);
    await this.state.resetAll();
    await this.assets.clearAll().catch(() => undefined);
    this.assets.setTargets([], []);
    void this.runPairing();
  }

  // --- orologio ------------------------------------------------------------

  private async syncClock(): Promise<void> {
    const off = await this.clock.sync(() => this.api.time());
    await this.state.setClockOffset(off);
    this.controller.assetsChanged(); // rivaluta con l'ora corretta
  }

  // --- manifest --------------------------------------------------------------

  private async fetchManifest(force: boolean): Promise<void> {
    if (!this.token) return;
    const r = await this.api.manifest(this.token, force ? null : this.lastReceivedEtag);
    if (r.kind === 'not_modified') return;
    const { manifest, warnings } = sanitizeManifest(r.manifest);
    for (const w of warnings.slice(0, 3)) this.errors.add(`manifest: ${w}`);
    if (!manifest) return; // spec sconosciuta o corrotto: si continua col precedente
    this.lastReceivedEtag = r.etag;
    const latest = this.pending?.manifest ?? this.current;
    if (latest && manifest.version === latest.version) {
      // stessa versione ma URL firmati nuovi: aggiorna solo gli URL dei download
      if (this.pending) this.pending = { ...this.pending, manifest, raw: r.manifest, etag: r.etag };
      else { this.current = manifest; this.currentRaw = r.manifest; this.currentEtag = r.etag; await this.persistCurrent(); }
      this.updateTargets();
      this.assets.retryNow();
      return;
    }
    this.pending = { manifest, raw: r.manifest, etag: r.etag, since: this.clock.now() };
    this.updateTargets();
    this.tryAdopt();
    void this.assets.gc().catch(() => undefined);
  }

  /** 403/scadenza sugli URL: manifest senza If-None-Match per URL nuovi, al massimo ogni 30 s. */
  private async refreshUrls(): Promise<void> {
    const now = Date.now();
    if (now - this.lastUrlRefresh < URL_REFRESH_MIN_GAP_MS) return;
    this.lastUrlRefresh = now;
    try {
      await this.fetchManifest(true);
    } catch (e) {
      this.onLoopError('manifest', e, 0);
    }
  }

  private adopt(m: Manifest, raw: unknown, etag: string | null): void {
    this.current = m;
    this.currentRaw = raw;
    this.currentEtag = etag;
    this.surfaces.setOrientation(m.device.orientation);
    this.updateTargets();
    this.controller.setManifest(m);
  }

  private async persistCurrent(): Promise<void> {
    if (this.currentRaw) await this.state.setManifest({ manifest: this.currentRaw, etag: this.currentEtag }).catch(() => undefined);
  }

  /** Passa al manifest in attesa quando gli asset delle sue voci attive sono in cache (API.md §3). */
  private tryAdopt(): void {
    const p = this.pending;
    if (!p || this.phase !== 'running') return;
    const hasCurrent = this.controller.state().current !== null;
    if (!canAdopt(p.manifest, (id, sha) => this.assets.isReady(id, sha), this.clock.now(), this.clock.now() - p.since, hasCurrent)) return;
    this.pending = null;
    this.adopt(p.manifest, p.raw, p.etag);
    void this.persistCurrent();
  }

  /** Ordine dei download: voci attive ora, poi fallback, poi le altre in ordine di palinsesto. */
  private updateTargets(): void {
    const primary = this.pending?.manifest ?? this.current;
    if (!primary) return;
    const byId = new Map<string, DownloadTarget>();
    const add = (m: Manifest | null) => {
      if (!m) return;
      for (const a of m.assets) {
        if (!byId.has(a.id)) byId.set(a.id, { id: a.id, url: a.url, sha256: a.sha256, size: a.size, mime: a.mime });
      }
    };
    const order: string[] = [];
    const push = (id: string) => { if (!order.includes(id)) order.push(id); };
    try { neededAssetIds(primary, this.clock.now()).forEach(push); } catch { /* manifest incoerente */ }
    if (primary.fallback_asset_id) push(primary.fallback_asset_id);
    for (const s of primary.schedule) push(s.asset_id);
    for (const a of primary.assets) push(a.id);
    add(primary);
    // il manifest in uso deve poter continuare a riprodursi: i suoi asset mancanti restano nella coda, in fondo
    if (primary !== this.current) for (const a of this.current?.assets ?? []) push(a.id);
    add(this.current);
    const targets = order.map((id) => byId.get(id)).filter((t): t is DownloadTarget => t !== undefined);
    // la versione "primary" ha la precedenza anche sugli URL
    for (const a of primary.assets) {
      const t = byId.get(a.id);
      if (t) { t.url = a.url; t.sha256 = a.sha256; t.size = a.size; t.mime = a.mime; }
    }
    const refs = [...(this.current?.assets ?? []), ...(this.pending?.manifest.assets ?? [])].map((a) => ({ id: a.id, sha256: a.sha256 }));
    this.assets.setTargets(targets, refs);
  }

  // --- heartbeat e comandi ---------------------------------------------------

  private async heartbeat(): Promise<void> {
    if (!this.token) return;
    const status = await this.buildStatus();
    let res: Awaited<ReturnType<ApiClient['heartbeat']>>;
    try {
      res = await this.api.heartbeat(this.token, {
        app_version: __APP_VERSION__,
        platform: this.platform,
        capabilities: this.caps,
        status,
      });
    } catch (e) {
      this.heartbeatOk = false;
      throw e;
    }
    this.heartbeatOk = true;
    this.lastHeartbeatOkAt = Date.now();
    // heartbeat riuscito: svuota il buffer dei play (a lotti; un lotto sparisce solo dopo un 200)
    try {
      await this.reporter?.flush();
    } catch (e) {
      if (e instanceof AuthError || e instanceof DeviceDisabledError || e instanceof SubscriptionInactiveError) throw e;
      this.errors.add(`plays: ${e instanceof Error ? e.message : String(e)}`);
    }
    const known = this.pending?.manifest.version ?? this.current?.version ?? null;
    if (res.manifest_version && res.manifest_version !== known) {
      void this.fetchManifest(false).catch((e) => this.onLoopError('manifest', e, 0));
    }
    for (const c of res.commands) await this.handleCommand(c);
  }

  private async buildStatus(): Promise<Record<string, unknown>> {
    const cs = this.controller.state();
    const est = await this.assets.estimate().catch(() => null);
    const MB = 1024 * 1024;
    const m = this.current;
    const missing = m ? m.assets.filter((a) => !this.assets.isReady(a.id, a.sha256)).map((a) => a.id) : [];
    return {
      manifest_version: m?.version ?? null,
      playing: cs.current && !cs.current.fallback && cs.current.scheduleId
        ? { schedule_id: cs.current.scheduleId, asset_id: cs.current.assetId } : null,
      cached_assets: this.assets.readyIds(),
      missing_assets: missing,
      disk_free_mb: est ? Math.round((est.quota - est.usage) / MB) : null,
      disk_total_mb: est ? Math.round(est.quota / MB) : null, // quota del browser, non il disco fisico
      uptime_s: Math.round(performance.now() / 1000),
      clock_offset_ms: this.clock.offset,
      errors: this.errors.list(),
      pending_manifest_version: this.pending?.manifest.version ?? null,
      store: this.store.kind,
      muted: this.surfaces.diag().muted,
    };
  }

  private async handleCommand(c: Command): Promise<void> {
    if (this.seenCommands.has(c.id)) return;
    this.seenCommands.add(c.id);
    switch (c.kind) {
      case 'reload':
        await this.fetchManifest(true);
        break;
      case 'clear_cache':
        await this.assets.clearAll();
        this.updateTargets();
        break;
      case 'restart':
        window.location.reload();
        break;
      case 'screenshot':
        await runScreenshotCommand({
          capture: () => captureJpeg(this.surfaces.snapshot(), this.surfaces.stageSize(), (w, h) => {
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            return canvas as unknown as CanvasLike;
          }),
          upload: async (blob, id) => {
            if (!this.token) throw new Error('nessun token');
            await this.api.screenshot(this.token, blob, id);
          },
          onError: (m) => this.errors.add(m),
        }, c.id);
        break;
      default:
        break; // comandi sconosciuti: ignorati
    }
  }

  // --- diagnostica -----------------------------------------------------------

  private diagLines(): DiagLines {
    const cs = this.controller ? this.controller.state() : null;
    const sd = this.surfaces.diag();
    const lines: DiagLines = [
      ['versione', `${__APP_VERSION__} (${this.platform})`],
      ['schermo', this.device ? `#${this.device.id} ${this.device.name}` : 'non abbinato'],
      ['api', this.cfg.apiBase],
      ['stato', `${this.phase}${cs ? ` / ${cs.phase} / ${cs.mode}` : ''}`],
      ['manifest', `${this.current?.version.slice(0, 12) ?? '-'}${this.pending ? ` (in attesa: ${this.pending.manifest.version.slice(0, 12)})` : ''}`],
      ['in onda', cs?.current ? `${cs.current.scheduleId ?? 'fallback'} ${cs.current.assetId} [${cs.current.kind}] slot ${cs.current.slot ?? '-'}` : 'nulla (nero)'],
      ['prossimo', cs?.nextKey ?? '-'],
      ['orologio', `offset ${this.clock.offset} ms${this.clock.hasOffset ? '' : ' (non stimato)'}`],
    ];
    if (this.assets) {
      lines.push(['cache', `${this.store.kind}: ${this.assets.readyIds().length} pronti, ${this.assets.pendingIds().length} da scaricare${this.assets.downloading ? `, in corso ${this.assets.downloading}` : ''}`]);
    }
    lines.push(['audio', sd.autoplayFallback ? 'muted (autoplay rifiutato)' : sd.muted ? 'muted' : 'attivo']);
    lines.push(['persist', this.storagePersisted === null ? 'n/d' : this.storagePersisted ? 'sì' : 'no']);
    if (!this.persistent) lines.push(['storage', 'IndexedDB non disponibile: stato solo in memoria']);
    if (cs?.failedAssets.length) lines.push(['asset KO', cs.failedAssets.join(', ')]);
    const errs = this.errors.list().slice(-3);
    errs.forEach((e, i) => lines.push([i === 0 ? 'errori' : '', e]));
    return lines;
  }

  private exposeDebugHooks(): void {
    const self = this;
    (window as unknown as { __player: unknown }).__player = {
      events: self.events.events,
      state() {
        const cs = self.controller.state();
        const phase = self.phase === 'running' ? cs.phase : self.phase;
        return {
          phase,
          appPhase: self.phase,
          manifestVersion: self.current?.version ?? null,
          pendingVersion: self.pending?.manifest.version ?? null,
          current: cs.current,
          schedule: (self.current?.schedule ?? []).map((s) => ({
            id: s.id, assetId: s.asset_id, repeat: s.repeat,
            kind: self.current?.assets.find((a) => a.id === s.asset_id)?.kind ?? 'video',
          })),
          store: self.store.kind,
          readyAssets: self.assets.readyIds(),
          cachedAssets: self.assets.readyIds(),
          online: self.heartbeatOk,
          lastHeartbeatOkAt: self.lastHeartbeatOkAt,
          deviceId: self.device?.id ?? null,
          clockOffsetMs: self.clock.offset,
          errors: self.errors.list(),
          audio: self.surfaces.diag(),
        };
      },
    };
  }
}
